import { VerificationStep, VerificationStatus, isPendingLikeStatus } from "../common/constant";
import { resolveTierListing } from "./standalone";

// Canonical home for `tierIdOf` as far as callers are concerned; the definition
// sits in ./tier so the standalone rules can share it without a cycle. Imported
// as well as re-exported — `export ... from` creates no local binding, and this
// module calls it.
import { tierIdOf } from "./tier";
export { tierIdOf };

const sortByNewest = (histories) =>
    [...(histories || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

/**
 * Newest history record per tier level, plus the failed→blocked relationship:
 * when a lower tier is `verification_failed` and a higher tier is still
 * `draft_verification`, that higher draft is "blocked" and must be cancelled
 * before the failed lower tier can be redone. Shared by all 3 layouts so this
 * logic cannot drift.
 */
export const resolveBlockedTier = (histories = []) => {
    const sorted = sortByNewest(histories);
    const latestByTierLevel = sorted.reduce((acc, h) => {
        if (h && !acc[h.tierLevel]) acc[h.tierLevel] = h;
        return acc;
    }, {});
    const levelHistories = Object.values(latestByTierLevel);
    const failedTierHistory =
        [...levelHistories].sort((a, b) => a.tierLevel - b.tierLevel).find((h) => h.status === "verification_failed") || null;
    const blockedTierHistory = failedTierHistory
        ? levelHistories.find((h) => h.tierLevel > failedTierHistory.tierLevel && h.status === "draft_verification") || null
        : null;
    return {
        blockedByFailedTier: !!(failedTierHistory && blockedTierHistory),
        failedTierHistory,
        blockedTierHistory,
        latestByTierLevel,
    };
};

/**
 * The single tier the client can act on right now, and why.
 *
 * Tiers must be completed in ascending order. Walking levels low→high:
 *   - `verified`                      → passed, keep walking up
 *   - pending / submitted             → awaiting review but does NOT block the
 *                                       next tier (product allows starting the
 *                                       next tier while the current is pending),
 *                                       so keep walking up
 *   - `verification_failed`/`canceled`→ THIS tier is actionable → { reason: "redo" }
 *   - `draft_verification`            → resume it → { reason: "resume" }
 *   - no record / anything else       → begin it → { reason: "start" }
 * If every leveled tier is verified/pending → { reason: "done", tier: null }.
 *
 * The critical property: a FAILED lower tier is returned the moment it is hit,
 * so a higher `verification_submitted` / `pending_verification` tier can never
 * mask a lower failure (that was the bug — the failed tier1 became unreachable
 * once tier2 was submitted). Higher submitted tiers are left untouched.
 */
export const resolveActionableTier = (tiers = [], histories = []) => {
    const latestByLevel = {};
    for (const h of sortByNewest(histories)) {
        const lvl = h?.tierLevel;
        if (Number.isFinite(lvl) && !(lvl in latestByLevel)) latestByLevel[lvl] = h;
    }

    const leveled = [...(tiers || [])]
        .filter((t) => Number.isFinite(t?.tierLevel))
        .sort((a, b) => a.tierLevel - b.tierLevel);

    for (const tier of leveled) {
        const status = latestByLevel[tier.tierLevel]?.status;
        if (status === VerificationStatus.VERIFIED) continue;
        if (isPendingLikeStatus(status)) continue; // awaiting review, not blocking
        if (status === "verification_failed" || status === "canceled")
            return { tier, tierId: tier._id, reason: "redo" };
        if (status === VerificationStatus.DRAFT)
            return { tier, tierId: tier._id, reason: "resume" };
        return { tier, tierId: tier._id, reason: "start" };
    }
    return { tier: null, tierId: null, reason: "done" };
};

/**
 * Decide which verification step to land on, derived purely from
 * profile + tiers + histories. Returns { step } and, when resuming a draft,
 * { selectedTierId } for the caller to apply via setSelectedTier.
 */
/**
 * Every tier the client has is verified — nothing outstanding, nothing in
 * review, nothing left to do ever. A tier with no record at all does not count,
 * so an untouched tier keeps this false.
 */
export const allTiersVerified = (tiers = [], histories = []) => {
    if (!tiers.length) return false;
    const newest = (tierId) =>
        sortByNewest(histories).find((h) => tierIdOf(h) === tierId) || null;
    return tiers.every(
        (tier) => newest(tier?._id)?.status === VerificationStatus.VERIFIED,
    );
};

export const resolveInitialStep = ({ profile, tiers = [], histories = [] }) => {
    const { blockedByFailedTier } = resolveBlockedTier(histories);
    const { showStandalone, ladderTiers } = resolveTierListing(tiers, histories);
    // Lowest tier the client can act on, and why (see resolveActionableTier).
    //
    // Ladder tiers only. Standalone tiers sit at tierLevel 0 so they sorted
    // first and could win the walk outright: a standalone in draft returned
    // `resume`, which landed the client straight in that one form and skipped
    // the tier list — so a second outstanding standalone was unreachable.
    const actionable = resolveActionableTier(ladderTiers, histories);

    // 1. Lower tier failed with a higher DRAFT above it → blocked (cancel flow).
    if (blockedByFailedTier) return { step: VerificationStep.Tier };
    // 2. An outstanding standalone is what the client was actually asked for, so
    //    it beats resuming a ladder draft and beats the "under review" screen.
    //    Always the Tier step, never straight into a form: there may be several,
    //    and the list is the only place that shows all of them.
    if (showStandalone) return { step: VerificationStep.Tier };
    // 3. The lowest actionable tier drives the landing step. `redo` (a failed
    //    lower tier) is surfaced even when higher tiers are already submitted,
    //    so it must beat the `hasPending` "under review" screen below.
    if (actionable.reason === "redo") return { step: VerificationStep.Tier };
    if (actionable.reason === "resume") return { step: VerificationStep.Document, selectedTierId: actionable.tierId };
    if (actionable.reason === "start" && profile?.flowSetting) return { step: VerificationStep.Tier };
    // 4. Everything verified → the status screen. That is the one state where a
    //    single message says all there is to say, and there is no list worth
    //    reading: no tier can be acted on again.
    if (allTiersVerified(tiers, histories)) return { step: VerificationStep.Status };
    // 5. Something is in review but not everything is verified → the tier LIST,
    //    not the generic under-review screen. The list carries a status tag per
    //    tier, so a client sees WHICH items are pending and which are done,
    //    rather than one sentence standing in for all of them. That matters now
    //    a case can ask for several standalone verifications at once.
    //
    //    `hasPending` is deliberately no longer a landing condition of its own.
    // 6. Fallbacks: resume the flow, pick a country, or start fresh.
    if (profile?.flowSetting) return { step: VerificationStep.Tier };
    if (profile?.status === "not_verified") return { step: VerificationStep.Country };
    return { step: VerificationStep.Flow };
};
